import { useEffect, useState } from "react";
import { FaCircleCheck } from "react-icons/fa6";
import { LuUserRound, LuCheck } from "react-icons/lu";

export default function BankDetailsStep({
    data = {},
    setData,
    getBankDetails,
    setPrefillBankData,
    setBankData,
    setIsBankValid,
    isBankValid,
    isBankVerified,
    loading,
    bankData,
    bankSuccess,
    prefillBankData
}) {
    const [errors, setErrors] = useState({});

    const validateField = (fieldName, val) => {
        const value = (val || "").toString().trim();
        if (fieldName === "name") {
            if (!value) return "Account Holder Name is required";
            if (/[@#$%^&*!~+=<>?/\\{}()[\]|`"';:,._0-9]/.test(value)) {
                return "Special characters and numbers are not allowed in name";
            }
            if (value.length < 3) return "Name must be at least 3 characters long";
            if (!/^[a-zA-Z\s]+$/.test(value)) return "Only alphabets are allowed";
            return "";
        }
        if (fieldName === "accountnumber") {
            if (!value) return "Account Number is required";
            if (/\s/.test(val)) return "Spaces are not allowed in Account Number";
            if (/\D/.test(value)) return "Only numbers are allowed in Account Number";
            if (value.length < 9 || value.length > 18) return "Account Number must be between 9 and 18 digits";
            return "";
        }
        if (fieldName === "ifsc") {
            const upper = value.toUpperCase();
            if (!upper) return "IFSC Code is required";
            if (/[@#$%^&*!~+=<>?/\\{}()[\]|`"';:,._\s-]/.test(val)) {
                return "Special characters (@, #, etc.) and spaces are not allowed";
            }
            if (upper.length < 4 && !/^[A-Z]+$/.test(upper)) {
                return "First 4 characters must be alphabets (e.g. SBIN, HDFC)";
            }
            if (upper.length >= 5 && upper[4] !== "0") {
                return "5th character of IFSC must be '0' (zero)";
            }
            if (upper.length < 11) {
                return "IFSC Code must be exactly 11 characters long";
            }
            if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(upper)) {
                return "Invalid IFSC format. Example: SBIN0001234";
            }
            return "";
        }
        if (fieldName === "accounttype") {
            if (!value) return "Account Type is required";
            return "";
        }
        return "";
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === "name") {
            formattedValue = value
                .replace(/[^a-zA-Z\s]/g, "")
                .replace(/^\s+/, "")
                .replace(/\s{2,}/g, " ")
                .slice(0, 70);
        } else if (name === "accountnumber") {
            formattedValue = value.replace(/\D/g, "").slice(0, 18);
        } else if (name === "ifsc") {
            formattedValue = value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "")
                .slice(0, 11);
        } else if (name === "accounttype") {
            formattedValue = value;
        }

        const fieldError = validateField(name, formattedValue);
        setErrors((prev) => ({
            ...prev,
            [name]: fieldError
        }));

        const updatedData = {
            ...(data || {}),
            [name]: formattedValue
        };

        if (setData) {
            setData(updatedData);
        }

        const nameValid = updatedData.name && updatedData.name.trim().length >= 3 && /^[a-zA-Z\s]+$/.test(updatedData.name.trim());
        const accValid = updatedData.accountnumber && /^[0-9]{9,18}$/.test(updatedData.accountnumber);
        const ifscValid = updatedData.ifsc && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(updatedData.ifsc);
        const typeValid = Boolean(updatedData.accounttype && updatedData.accounttype.trim());

        const isValid = nameValid && accValid && ifscValid && typeValid;

        if (setIsBankValid) {
            setIsBankValid(Boolean(isValid));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const fieldError = validateField(name, value);
        setErrors((prev) => ({
            ...prev,
            [name]: fieldError
        }));
    };

    // Live bank lookup when valid 11-digit IFSC code is entered
    useEffect(() => {
        const ifscCode = (data?.ifsc || "").toString().trim().toUpperCase();
        if (ifscCode.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
            const fetchBankDetailsFromIFSC = async () => {
                try {
                    const res = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
                    if (res.ok) {
                        const bankInfo = await res.json();
                        if (bankInfo?.BANK && setData) {
                            setData((prev) => ({
                                ...(prev || data || {}),
                                bankname: bankInfo.BANK || prev?.bankname || "",
                                branchname: bankInfo.BRANCH || prev?.branchname || ""
                            }));
                            setErrors((prev) => ({ ...prev, ifsc: "" }));
                        }
                    } else if (res.status === 404) {
                        setErrors((prev) => ({
                            ...prev,
                            ifsc: "Invalid IFSC Code. Bank not found in database."
                        }));
                    }
                } catch (err) {
                    console.log("IFSC lookup network error:", err);
                }
            };
            fetchBankDetailsFromIFSC();
        }
    }, [data?.ifsc]);

    // Initial fetch if getBankDetails function is provided
    useEffect(() => {
        if (typeof getBankDetails === "function" && (!data?.accountnumber && !data?.name && !data?.ifsc)) {
            getBankDetails().then((res) => {
                if (res) {
                    const apiData = res?.data?.[0] || res?.data || res;
                    const accNum = apiData?.accountnumber || apiData?.bankaccount || apiData?.account_number || apiData?.bank_account || apiData?.account_no || "";
                    if (accNum && setData) {
                        setData((prev) => ({
                            ...(prev || data || {}),
                            accountnumber: accNum,
                            ifsc: apiData?.ifsc || apiData?.ifsc_code || prev?.ifsc || "",
                            name: apiData?.name || apiData?.beneficiaryname || apiData?.beneficiary_name || prev?.name || "",
                            accounttype: apiData?.accounttype || apiData?.account_type || prev?.accounttype || "savings",
                            bankname: apiData?.bank_name || apiData?.bankname || prev?.bankname || "",
                            branchname: apiData?.branch_name || apiData?.branchname || prev?.branchname || "",
                        }));
                    }
                }
            }).catch(console.error);
        }
    }, []);

    useEffect(() => {
        if (prefillBankData && setData) {
            const accNum = prefillBankData?.accountnumber || prefillBankData?.bankaccount || prefillBankData?.account_number || prefillBankData?.bank_account || prefillBankData?.account_no || "";
            const ifscCode = prefillBankData?.ifsc || prefillBankData?.ifsc_code || "";
            const holderName = prefillBankData?.name || prefillBankData?.beneficiaryname || prefillBankData?.beneficiary_name || "";
            const accType = prefillBankData?.accounttype || prefillBankData?.account_type || "";
            const bName = prefillBankData?.bank_name || prefillBankData?.bankname || "";
            const brName = prefillBankData?.branch_name || prefillBankData?.branchname || "";

            if (accNum || ifscCode || holderName) {
                setData((prev) => ({
                    ...(prev || data || {}),
                    accountnumber: accNum || prev?.accountnumber || "",
                    ifsc: ifscCode || prev?.ifsc || "",
                    name: holderName || prev?.name || "",
                    accounttype: accType || prev?.accounttype || "savings",
                    bankname: bName || prev?.bankname || "",
                    branchname: brName || prev?.branchname || "",
                }));
            }
        }
    }, [prefillBankData]);

    return (
        <div className="w-100">
            <style>{`
                .bank-details-input:focus {
                    background-color: #fff !important;
                    border-color: #f97316 !important;
                    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.15) !important;
                }
                .custom-bank-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 99999;
                    padding: 16px;
                }
                .custom-bank-modal {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 24px;
                    max-width: 380px;
                    width: 100%;
                    text-align: center;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }
            `}</style>

            <div className="d-flex align-items-center justify-content-between mt-3 mb-2 flex-wrap" style={{ gap: "8px" }}>
                <div>
                    <h2 className="h5 fw-bold mb-1" style={{ color: "#0f172a" }}>
                        Enter Bank Details
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                        Provide active bank account details for settlements and payouts
                    </p>
                </div>
            </div>

            <div className="card shadow-sm p-3 p-sm-4 border-0 mt-2 mb-3" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
                {isBankVerified && Boolean(data?.name && data?.accountnumber && data?.ifsc) ? (
                    <div
                        className="alert alert-success d-flex align-items-center mb-3 p-2.5 p-sm-3"
                        style={{
                            borderRadius: "12px",
                            gap: "12px",
                            border: "1px solid #86efac",
                            background: "#f0fdf4"
                        }}
                    >
                        <FaCircleCheck size={22} className="text-success flex-shrink-0" />
                        <div>
                            <strong style={{ display: "block", fontSize: "14px", color: "#166534" }}>
                                Bank Details Verified Successfully
                            </strong>
                            <span style={{ fontSize: "12px", color: "#15803d" }}>
                                Your bank account details are verified and locked.
                            </span>
                        </div>
                    </div>
                ) : null}

                <div className="card-body p-0">
                    <div className="d-flex align-items-center mb-3" style={{ gap: "8px" }}>
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{ width: "32px", height: "32px", background: "#fff7ed", color: "#ea580c" }}
                        >
                            <LuUserRound size={18} />
                        </div>
                        <h3 className="h6 fw-bold mb-0" style={{ color: "#1e293b", fontSize: "16px" }}>
                            Account Information
                        </h3>
                    </div>

                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px", display: "block" }}>
                                Account Holder Name <span className="text-danger">*</span>
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="text"
                                    name="name"
                                    maxLength={70}
                                    disabled={isBankVerified}
                                    value={data?.name || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onKeyDown={(e) => {
                                        if (e.key === " " && (!data?.name || data?.name.endsWith(" "))) {
                                            e.preventDefault();
                                        }
                                        if (!/[a-zA-Z\s]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="form-control bank-details-input pe-5"
                                    placeholder="Full Name as per bank records"
                                    style={{
                                        background: isBankVerified ? "#e9ecef" : "#F8FAFC",
                                        cursor: isBankVerified ? "not-allowed" : "text",
                                        height: "46px",
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        borderColor: errors.name ? "#EF4444" : "#E2E8F0"
                                    }}
                                />

                                {isBankVerified && Boolean(data?.name && data?.name.toString().trim()) ? (
                                    <LuCheck
                                        style={{
                                            position: "absolute",
                                            right: "14px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#16a34a",
                                            fontSize: "18px",
                                            pointerEvents: "none"
                                        }}
                                    />
                                ) : null}
                            </div>
                            {errors.name && (
                                <span className="text-danger" style={{ fontSize: "12px", marginTop: "4px", display: "block", fontWeight: 500 }}>
                                    {errors.name}
                                </span>
                            )}
                        </div>

                        <div className="col-12 col-md-6">
                            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px", display: "block" }}>
                                IFSC Code <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    name="ifsc"
                                    maxLength={11}
                                    disabled={isBankVerified}
                                    value={data?.ifsc || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onKeyDown={(e) => {
                                        if (e.key === " " || /[@#$%^&*!~+=<>?/\\{}()[\]|`"';:,._-]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                        if (!/[a-zA-Z0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pasted = (e.clipboardData?.getData("text") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
                                        handleChange({ target: { name: "ifsc", value: pasted } });
                                    }}
                                    className="form-control bank-details-input pe-5"
                                    placeholder="e.g. SBIN0001234"
                                    style={{
                                        background: isBankVerified ? "#e9ecef" : "#F8FAFC",
                                        cursor: isBankVerified ? "not-allowed" : "text",
                                        textTransform: "uppercase",
                                        height: "46px",
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        borderColor: errors.ifsc ? "#EF4444" : "#E2E8F0"
                                    }}
                                />
                                {isBankVerified && Boolean(data?.ifsc && data?.ifsc.toString().trim()) ? (
                                    <LuCheck
                                        style={{
                                            position: "absolute",
                                            right: "14px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#16a34a",
                                            fontSize: "18px",
                                            pointerEvents: "none"
                                        }}
                                    />
                                ) : null}
                            </div>
                            {errors.ifsc ? (
                                <span className="text-danger" style={{ fontSize: "12px", marginTop: "4px", display: "block", fontWeight: 500 }}>
                                    {errors.ifsc}
                                </span>
                            ) : data?.bankname ? (
                                <div
                                    className="mt-1.5 p-2 rounded d-flex align-items-center"
                                    style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", gap: "6px" }}
                                >
                                    <span style={{ fontSize: "14px" }}>🏛️</span>
                                    <span style={{ fontSize: "12px", color: "#166534", fontWeight: 600, wordBreak: "break-word" }}>
                                        {data.bankname} {data.branchname ? `• ${data.branchname}` : ""}
                                    </span>
                                </div>
                            ) : null}
                        </div>

                        <div className="col-12 col-md-6">
                            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px", display: "block" }}>
                                Account Number <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    name="accountnumber"
                                    maxLength={18}
                                    disabled={isBankVerified}
                                    value={data?.accountnumber || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onKeyDown={(e) => {
                                        if (e.key === " ") {
                                            e.preventDefault();
                                        }
                                        if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="form-control bank-details-input pe-5"
                                    placeholder="9-18 digit account number"
                                    style={{
                                        background: isBankVerified ? "#e9ecef" : "#F8FAFC",
                                        cursor: isBankVerified ? "not-allowed" : "text",
                                        height: "46px",
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        borderColor: errors.accountnumber ? "#EF4444" : "#E2E8F0"
                                    }}
                                />
                                {isBankVerified && Boolean(data?.accountnumber && data?.accountnumber.toString().trim()) ? (
                                    <LuCheck
                                        style={{
                                            position: "absolute",
                                            right: "14px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#16a34a",
                                            fontSize: "18px",
                                            pointerEvents: "none"
                                        }}
                                    />
                                ) : null}
                            </div>
                            {errors.accountnumber && (
                                <span className="text-danger" style={{ fontSize: "12px", marginTop: "4px", display: "block", fontWeight: 500 }}>
                                    {errors.accountnumber}
                                </span>
                            )}
                        </div>

                        <div className="col-12 col-md-6">
                            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px", display: "block" }}>
                                Account Type <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <select
                                    name="accounttype"
                                    disabled={isBankVerified}
                                    value={data?.accounttype || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control bank-details-input"
                                    style={{
                                        background: isBankVerified ? "#e9ecef" : "#F8FAFC",
                                        cursor: isBankVerified ? "not-allowed" : "pointer",
                                        height: "46px",
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        borderColor: errors.accounttype ? "#EF4444" : "#E2E8F0"
                                    }}
                                >
                                    <option value="">Select Account Type</option>
                                    <option value="savings">Savings Account</option>
                                    <option value="current">Current Account</option>
                                </select>
                                {isBankVerified && Boolean(data?.accounttype && data?.accounttype.toString().trim()) ? (
                                    <LuCheck
                                        style={{
                                            position: "absolute",
                                            right: "30px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: "#16a34a",
                                            fontSize: "18px",
                                            pointerEvents: "none"
                                        }}
                                    />
                                ) : null}
                            </div>
                            {errors.accounttype && (
                                <span className="text-danger" style={{ fontSize: "12px", marginTop: "4px", display: "block", fontWeight: 500 }}>
                                    {errors.accounttype}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="custom-bank-modal-overlay">
                    <div className="custom-bank-modal">
                        <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                            style={{ width: "2.5rem", height: "2.5rem", color: "#f97316" }}
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                        <h4 className="h6 fw-bold mb-1" style={{ color: "#0f172a" }}>
                            Verifying bank account...
                        </h4>
                        <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
                            This usually takes few seconds
                        </p>
                        <span className="badge bg-light text-secondary py-1.5 px-2" style={{ fontSize: "11px" }}>
                            🔒 Details are securely processed
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
